import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from "react";
import { X } from "@phosphor-icons/react";
import styles from "./Dialog.module.css";

export interface DialogHandle {
  open(): void;
  close(): void;
}

interface DialogProps {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  className?: string;
}

export const Dialog = forwardRef<DialogHandle, DialogProps>(function Dialog(
  { title, children, onClose, className },
  ref,
) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useImperativeHandle(ref, () => ({
    open: () => dialogRef.current?.showModal(),
    close: () => dialogRef.current?.close(),
  }));

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose?.();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) dialogRef.current?.close();
  };

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, className].filter(Boolean).join(" ")}
      onClick={handleBackdropClick}
    >
      <button
        type="button"
        className={styles.close}
        aria-label="Close"
        onClick={() => dialogRef.current?.close()}
      >
        <X size={20} aria-hidden="true" />
      </button>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.body}>{children}</div>
    </dialog>
  );
});
